import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx } from "react/jsx-runtime";
import { nanoid } from "nanoid/non-secure";
import * as React from "react";
import useLatestCallback from "use-latest-callback";
import NavigationHelpersContext from "./NavigationHelpersContext";
import NavigationRouteContext from "./NavigationRouteContext";
import PreventRemoveContext from "./PreventRemoveContext";
/**
 * Util function to transform map of prevented routes to a simpler object.
 */ var transformPreventedRoutes = function(preventedRoutesMap) {
    var preventedRoutesToTransform = _to_consumable_array(preventedRoutesMap.values());
    var preventedRoutes = preventedRoutesToTransform.reduce(function(acc, param) {
        var routeKey = param.routeKey, preventRemove = param.preventRemove;
        var _acc_routeKey;
        acc[routeKey] = {
            preventRemove: ((_acc_routeKey = acc[routeKey]) === null || _acc_routeKey === void 0 ? void 0 : _acc_routeKey.preventRemove) || preventRemove
        };
        return acc;
    }, {});
    return preventedRoutes;
};
/**
 * Component used for managing which routes have to be prevented from removal in native-stack.
 */ export default function PreventRemoveProvider(param) {
    var children = param.children;
    var _React_useState = _sliced_to_array(React.useState(function() {
        return nanoid();
    }), 1), parentId = _React_useState[0];
    var _React_useState1 = _sliced_to_array(React.useState(new Map()), 2), preventedRoutesMap = _React_useState1[0], setPreventedRoutesMap = _React_useState1[1];
    var navigation = React.useContext(NavigationHelpersContext);
    var route = React.useContext(NavigationRouteContext);
    var preventRemoveContextValue = React.useContext(PreventRemoveContext);
    // take `setPreventRemove` from parent context - if exist it means we're in a nested context
    var setParentPrevented = preventRemoveContextValue === null || preventRemoveContextValue === void 0 ? void 0 : preventRemoveContextValue.setPreventRemove;
    var setPreventRemove = useLatestCallback(function(id, routeKey, preventRemove) {
        if (preventRemove && (navigation == null || (navigation === null || navigation === void 0 ? void 0 : navigation.getState().routes.every(function(route) {
            return route.key !== routeKey;
        })))) {
            throw new Error("Couldn't find a route with the key ".concat(routeKey, ". Is your component inside NavigationContent?"));
        }
        setPreventedRoutesMap(function(prevPrevented) {
            var _prevPrevented_get, _prevPrevented_get1;
            // values haven't changed - do nothing
            if (routeKey === ((_prevPrevented_get = prevPrevented.get(id)) === null || _prevPrevented_get === void 0 ? void 0 : _prevPrevented_get.routeKey) && preventRemove === ((_prevPrevented_get1 = prevPrevented.get(id)) === null || _prevPrevented_get1 === void 0 ? void 0 : _prevPrevented_get1.preventRemove)) {
                return prevPrevented;
            }
            var nextPrevented = new Map(prevPrevented);
            if (preventRemove) {
                nextPrevented.set(id, {
                    routeKey: routeKey,
                    preventRemove: preventRemove
                });
            } else {
                nextPrevented.delete(id);
            }
            return nextPrevented;
        });
    });
    var isPrevented = _to_consumable_array(preventedRoutesMap.values()).some(function(param) {
        var preventRemove = param.preventRemove;
        return preventRemove;
    });
    React.useEffect(function() {
        if ((route === null || route === void 0 ? void 0 : route.key) !== undefined && setParentPrevented !== undefined) {
            // when route is defined (and setParentPrevented) it means we're in a nested stack
            // route.key then will be the route key of parent
            setParentPrevented(parentId, route.key, isPrevented);
            return function() {
                setParentPrevented(parentId, route.key, false);
            };
        }
        return;
    }, [
        parentId,
        isPrevented,
        route === null || route === void 0 ? void 0 : route.key,
        setParentPrevented
    ]);
    var value = React.useMemo(function() {
        return {
            setPreventRemove: setPreventRemove,
            preventedRoutes: transformPreventedRoutes(preventedRoutesMap)
        };
    }, [
        setPreventRemove,
        preventedRoutesMap
    ]);
    return /*#__PURE__*/ _jsx(PreventRemoveContext.Provider, {
        value: value,
        children: children
    });
}
